import React, { useMemo, useRef, useEffect, useState } from "react";

// Hooks
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { useRealtimeDrivers, useRealtimeTags } from 'Core/hooks';
import { useNavigate, useLocation } from "react-router-dom";

// Controls
import { Tabs, TabContainer, Container } from "@gpstrackit/gtc-react-controls";
import { NotificationManager } from 'Components/Notifications';

// Utils
import { HasPermission } from "Core/security";

// Tabs
import DriverPerformanceDriverView from "../driver";
import DriverPerformanceDashboardView from "../../views/dashboard";
import DriverPerformanceEventsView from "../../views/events";
import DriverPerformanceCasesView from "../../views/cases";

// Redux Actions
import { openModalViewCreationPanel } from "Modules/driverPerformance/views/cases/modals/viewCreationPanel/redux/slice";
import { setDriverPerformanceMainTab, clearRedux, catalogLoad } from "Redux/actions";
import { searchDriverPerformanceCasesCoach } from "Modules/driverPerformance/views/cases/modals/advancedFilters/redux/slice";
import { resetEditCoachPanel } from "Modules/driverPerformance/views/cases/modals/editCoachPanel/redux/slice";

import ViewCreationPanel from "Modules/driverPerformance/views/cases/modals/viewCreationPanel";
import { updateEventsWithCasesBulk } from "Modules/driverPerformance/views/events/redux/slice";
import EditCoachPanel from 'Modules/driverPerformance/views/cases/modals/editCoachPanel';

//Styles
import "./index.scss";

const DriverPerformanceMainView = () => {

  //Language
  const { messages } = useIntl();

  //Redux
  const dataCreationCasesPerformance = useSelector((state) => state.catalogsRedux.dataCreationCasesPerformance);
  const openModalViewCreation = useSelector((state) => state.driverPerformanceCasesTabViewCreationPanelRedux.open);
  const openModalEditCoach = useSelector(state => state.driverPerformanceCasesTabEditCoachRedux.open);
  const updateCoach = useSelector(state => state.driverPerformanceCasesTabEditCoachRedux.updateCoach);
  const { coaches, isLoaded } = useSelector((state) => state.driverPerformanceCasesTabAdvancedFiltersRedux);
  const drivers = useRealtimeDrivers({ trackFields: ['id', 'name', 'externalId', 'disabled', 'tags'] });
  const tags = useRealtimeTags({ trackFields: ['id', 'label', 'disabled', 'tagPath', 'tags'] });
  const { tab } = useSelector((state) => state.driverPerformanceMainRedux);
  const user = useSelector((state) => state.securityRedux.user);
  const tabsRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [loadDrivers, setLoadDrivers] = useState(false);
  const [loadTags, setLoadTags] = useState(false);

  //Tabs
  const tabs = [
    {
      label: messages['driver_performance_dashboard'],
      master: "driverPer",
      subUser: [316],
      component: <DriverPerformanceDashboardView />
    },
    {
      label: messages['driver_performance_driver'],
      master: "driverPer",
      subUser: [316],
      component: <DriverPerformanceDriverView />
    },
    {
      label: messages['driver_performance_events'],
      master: "driverPer",
      subUser: [316],
      component: <DriverPerformanceEventsView />
    },
    {
      label: messages['driver_performance_cases'],
      master: "driverPer",
      subUser: [316],
      component: <DriverPerformanceCasesView />
    }
  ];


  const filteredTabs = useMemo(() => [
    ...tabs.filter((tab) => HasPermission(user, tab.master, tab.subUser))
  ], [user, tabs]);

  // Effect to change the tab based on the URL parameter
  useEffect(() => {
    const urlSearchParams = new URLSearchParams(location.search);
    const tabParam = urlSearchParams.get('tab');

    if (tabParam === 'cases') {
      dispatch(setDriverPerformanceMainTab(3));
    } else if (tabParam === 'drivers') {
      dispatch(setDriverPerformanceMainTab(1));
    } else if (tabParam === 'events') {
      dispatch(setDriverPerformanceMainTab(2));
    } else if (tabParam === 'dashboard') {
      dispatch(setDriverPerformanceMainTab(0));
    }
  }, [location.search, dispatch]);

  useEffect(() => {
    tabsRef.current?.setValue(tab);

    try {
      const urlSearchParams = new URLSearchParams(location.search);
      let shouldUpdateUrl = false;

      // Clear tab parameter if it doesn't match the current tab
      const tabParam = urlSearchParams.get('tab');
      if (tabParam) {
        if ((tab !== 3 && tabParam === 'cases') ||
          (tab !== 2 && tabParam === 'events') ||
          (tab !== 1 && tabParam === 'drivers') ||
          (tab !== 0 && tabParam === 'dashboard')) {
          urlSearchParams.delete('tab');
          shouldUpdateUrl = true;
        }
      }

      // Clear Events filter parameters when not in the Events tab (tab 2)
      if (tab !== 2) {
        const eventsFilterParams = [
          'eventsDriverId',
          'eventsTagId',
          'eventsNames',
          'eventsTags',
          'eventsRange',
          'eventsStartDate',
          'eventsEndDate'
        ];

        eventsFilterParams.forEach(param => {
          if (urlSearchParams.has(param)) {
            urlSearchParams.delete(param);
            shouldUpdateUrl = true;
          }
        });
      }

      // Clear Cases filter parameters when not in the Cases tab (tab 3)
      if (tab !== 3) {
        const casesFilterParams = ['range', 'startDate', 'endDate', 'statusCases', 'caseNumbers', 'showCreateModal', 'casesDriverIds', 'coachId'];
        casesFilterParams.forEach(param => {
          if (urlSearchParams.has(param)) {
            urlSearchParams.delete(param);
            shouldUpdateUrl = true;
          }
        });
      }

      // Clear Drivers filter parameters when not in the Drivers tab (tab 1)
      if (tab !== 1) {
        const driversFilterParams = [
          'driversRange',
          'driversStartDate',
          'driversEndDate'
          // Add other driver-specific filters here if they exist
        ];

        driversFilterParams.forEach(param => {
          if (urlSearchParams.has(param)) {
            urlSearchParams.delete(param);
            shouldUpdateUrl = true;
          }
        });
      }

      // Update URL only if there are changes
      if (shouldUpdateUrl) {
        const newSearch = urlSearchParams.toString();
        const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
        navigate(newUrl, { replace: true });
      }
    } catch (error) {
      console.error('Error cleaning URL parameters:', error);
    }
  }, [tab, navigate, location.pathname, location.search])

  useEffect(() => {
    if (dataCreationCasesPerformance) {
      if (user?.subUserId && user?.subUserId != dataCreationCasesPerformance?.userId) {
        dispatch(clearRedux("CATALOG_REDUX", { dataCreationCasesPerformance: null }));
        return;
      } else {
        NotificationManager.success(
          messages['success'],
          messages['driver_performance_cases_view_saveSuccessfully'],
          3000,
          null,
          null,
          'success',
        );
        const dataCreation = {
          dateExecuted: dataCreationCasesPerformance?.dateExecuted || 0,
          timeExecutedMinutes: `${dataCreationCasesPerformance?.timeExecutedMinutes} ${messages['driver_performance_cases_minutes']}`,
          casesCreated: dataCreationCasesPerformance?.casesCreated || 0,
          casesUpdated: dataCreationCasesPerformance?.casesUpdated || 0,
          casesNoCoachAssigned: dataCreationCasesPerformance?.casesNoCoachAssigned || 0,
          eventsIgnoredNoDriver: dataCreationCasesPerformance?.eventsIgnoredNoDriver || 0,
          eventsIgnoredIncompatibleStatus: dataCreationCasesPerformance?.eventsIgnoredIncompatibleStatus || 0,
          cases: dataCreationCasesPerformance?.cases || []
        }
        //update events with the new data for cases created
        dispatch(updateEventsWithCasesBulk(dataCreationCasesPerformance));
        dispatch(openModalViewCreationPanel({ open: !openModalViewCreation, dataCreation }));
        dispatch(clearRedux("CATALOG_REDUX", { dataCreationCasesPerformance: null }));
      }
    }
  }, [dataCreationCasesPerformance]);

  useEffect(() => {
    if (!drivers?.length && !loadDrivers) {
      dispatch(catalogLoad(['drivers']))
      setLoadDrivers(true)
    }
    if (!tags?.length && !loadTags) {
      dispatch(catalogLoad(['tags']))
      setLoadTags(true)
    }
  }, [drivers, tags])

  useEffect(() => {
    if (!isLoaded) {
      dispatch(searchDriverPerformanceCasesCoach({}))
    }
  }, [isLoaded])

  return (
    <Container maxWidth={false} className="container-view">
      <div className="container-view-dr-performance">
        <Tabs
          ref={tabsRef}
          tab={tab}
          onChange={(value) => dispatch(setDriverPerformanceMainTab(value))}
        >
          {filteredTabs.map((item, index) => {
            const { label, component } = item;
            return (
              <TabContainer key={index} label={label} index={index}>
                {component}
              </TabContainer>
            );
          })}
        </Tabs>
        <ViewCreationPanel />
        <EditCoachPanel />
      </div>
    </Container >
  );
};

export default DriverPerformanceMainView;
